import {
  Briefcase,
  CircleDollarSign,
  Compass,
  Landmark,
  ListChecks,
  type LucideIcon,
  Users,
  UsersRound,
  Waypoints,
} from 'lucide-react';
import { NewVersionBanner } from './ui/NewVersionBanner.js';
import { useEffect } from 'react';
import { Link, Redirect, Route, Switch, useLocation } from 'wouter';
import { AssistantPanel } from './assistant/AssistantPanel.js';
import { AssistantButton, useAssistantShell } from './assistant/shell.js';
import { nextAfterSignIn } from './auth/nextAfterSignIn.js';
import { SignIn } from './auth/SignIn.js';
import { useSession, useSignOut } from './auth/useSession.js';
import { DiscoverPage } from './DiscoverPage.js';
import { FundsPage } from './FundsPage.js';
import { InvestorsPage } from './InvestorsPage.js';
import { ListPage } from './lists/ListPage.js';
import { ListsPage } from './lists/ListsPage.js';
import { PortfolioPage } from './portfolio/PortfolioPage.js';
import { RoundsPage } from './RoundsPage.js';
import { TeamPage } from './team/TeamPage.js';
import { useDriveMenuItems } from './sheets/useDriveMenuItems.js';
import { Menu } from './ui/Menu.js';

/** What each route reads in the tab, ahead of the product name. */
const PAGE_TITLES: Record<string, string> = {
  '/': 'Funds',
  '/investors': 'Investors',
  '/rounds': 'Rounds',
  '/discover': 'Discover',
  '/portfolio': 'Portfolio',
  '/lists': 'Lists',
  '/team': 'Team',
};

/** The header's tabs, each with the icon that marks it. */
const NAV: readonly { href: string; label: string; icon: LucideIcon }[] = [
  { href: '/', label: 'Funds', icon: Landmark },
  { href: '/investors', label: 'Investors', icon: Users },
  { href: '/rounds', label: 'Rounds', icon: CircleDollarSign },
  { href: '/discover', label: 'Discover', icon: Compass },
  { href: '/portfolio', label: 'Portfolio', icon: Briefcase },
  { href: '/lists', label: 'Lists', icon: ListChecks },
  { href: '/team', label: 'Team', icon: UsersRound },
];

export function App(): React.JSX.Element {
  const session = useSession();
  const signOut = useSignOut();
  const [location] = useLocation();

  // Signing in was a detour from the MCP authorize page: go back to it rather
  // than showing the grid.
  const next = nextAfterSignIn();
  const resuming = next !== null && session.data !== null && session.data !== undefined;
  useEffect(() => {
    if (resuming && next !== null) window.location.replace(next);
  }, [resuming, next]);

  const signedIn = session.data !== null && session.data !== undefined;
  const driveItems = useDriveMenuItems(signedIn);
  const assistant = useAssistantShell(signedIn);
  useEffect(() => {
    const page = signedIn
      ? (PAGE_TITLES[location] ?? (location.startsWith('/lists/') ? 'List' : undefined))
      : undefined;
    document.title = page === undefined ? 'Follow-On Investors' : `${page} · Follow-On Investors`;
  }, [location, signedIn]);

  if (session.isPending) return <p className="subtle loading">…</p>;
  if (session.data === null || session.data === undefined) return <SignIn />;
  if (resuming) return <p className="subtle loading">…</p>;

  return (
    <div className="app">
      <NewVersionBanner />
      <header className="app-head">
        <strong className="brand">
          <span className="brand-mark" aria-hidden="true">
            <Waypoints />
          </span>
          Follow-On Investors
        </strong>
        <nav>
          {NAV.map(({ href, label, icon: Icon }) => (
            <Link
              key={href}
              href={href}
              className={
                (href === '/' ? location === '/' : location.startsWith(href)) ? 'active' : ''
              }
            >
              <Icon aria-hidden="true" />
              {label}
            </Link>
          ))}
        </nav>
        <span className="spacer" />
        <AssistantButton shell={assistant} />
        <Menu
          label={`${session.data.name}, account menu`}
          className="user-button"
          items={[...driveItems, { label: 'Sign out', onSelect: () => signOut.mutate() }]}
        >
          {session.data.name}
        </Menu>
      </header>

      <div className="app-body">
        <div className="app-page">
          <Switch>
            <Route path="/investors" component={InvestorsPage} />
            <Route path="/rounds" component={RoundsPage} />
            <Route path="/discover" component={DiscoverPage} />
            <Route path="/candidates">
              <Redirect to="/discover" replace />
            </Route>
            <Route path="/portfolio" component={PortfolioPage} />
            <Route path="/lists" component={ListsPage} />
            <Route path="/lists/:id" component={ListPage} />
            <Route path="/team" component={TeamPage} />
            <Route path="/" component={FundsPage} />
            <Route>
              <NoSuchPage />
            </Route>
          </Switch>
        </div>
        {assistant.open && <AssistantPanel onClose={assistant.close} />}
      </div>
    </div>
  );
}

function NoSuchPage() {
  return <p className="subtle">No such page.</p>;
}
